import { createStore } from "vuex";
import api from "@/helpers/api";

function safeParseUser() {
  const raw = localStorage.getItem("userdata");
  if (!raw || raw === "undefined" || raw === "null") return null;
  try {
    return JSON.parse(raw);
  } catch {
    localStorage.removeItem("userdata");
    return null;
  }
}

/**
 * Best-effort POST /auth/logout, which revokes the session named in the
 * token. Never awaited: sign-out must not wait on, or fail because of, the
 * network. The token is passed explicitly because local storage is cleared
 * before the request goes out.
 */
function endServerSession(token) {
  api
    .post("/auth/logout", {}, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 8000,
      meta: { ignoreOutage: true },
    })
    .catch(() => {});
}

export default createStore({
  state: {
    currentUser: safeParseUser(),
    token: localStorage.getItem("jwt") || null,
  },

  getters: {
    isAuthenticated: (state) => !!state.token,
    currentUser: (state) => state.currentUser,
    userRole: (state) => {
      if (!state.currentUser) return "";
      return state.currentUser.role || (state.currentUser.roles || [])[0] || "";
    },
  },

  mutations: {
    SET_AUTH(state, { token, user }) {
      state.token = token;
      state.currentUser = user;
    },
    CLEAR_AUTH(state) {
      state.token = null;
      state.currentUser = null;
    },
    UPDATE_PROFILE_IMAGE(state, url) {
      if (state.currentUser) {
        state.currentUser = { ...state.currentUser, profileImageUrl: url };
        localStorage.setItem("userdata", JSON.stringify(state.currentUser));
      }
    },
    UPDATE_USER_FIELDS(state, fields) {
      if (state.currentUser && fields && typeof fields === "object") {
        state.currentUser = { ...state.currentUser, ...fields };
        localStorage.setItem("userdata", JSON.stringify(state.currentUser));
      }
    },
  },

  actions: {
    /**
     * Login with email + password. Stores JWT and user data.
     * @param {Object} credentials - { email, password }
     * @returns {Object} user data from API
     */
    async login({ commit, dispatch }, credentials) {
      dispatch("logout");
      const response = await api.post("/auth/login", credentials);
      const { token, refreshToken, user } = response.data;

      localStorage.setItem("jwt", token);
      if (refreshToken) {
        localStorage.setItem("refreshToken", refreshToken);
      }
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("userdata", JSON.stringify(user));

      commit("SET_AUTH", { token, user });
      return user;
    },

    /**
     * JC Login with mobile + password.
     */
    async jcLogin({ commit, dispatch }, { mobile, password, userType }) {
      // Drop any cached auth from a previous session BEFORE storing the new
      // user — otherwise stale userdata (different employer/seeker, different
      // companyId) leaks into the first render after sign-in.
      dispatch("logout");

      const response = await api.post("/jc-auth/login", { mobile: String(mobile), password, userType });
      const { token, refreshToken, user, companyId } = response.data;

      localStorage.setItem("jwt", token);
      if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("userdata", JSON.stringify(user));
      if (companyId) localStorage.setItem("companyId", String(companyId));

      commit("SET_AUTH", { token, user });
      return user;
    },

    /**
     * JC OTP Login — verify OTP and authenticate.
     */
    async jcVerifyOtpLogin({ commit, dispatch }, { mobile, otp, userType }) {
      dispatch("logout");
      const response = await api.post("/jc-auth/verify-otp-login", { mobile: String(mobile), otp: String(otp), userType });
      const { token, refreshToken, user, companyId } = response.data;

      localStorage.setItem("jwt", token);
      if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("userdata", JSON.stringify(user));
      if (companyId) localStorage.setItem("companyId", String(companyId));

      commit("SET_AUTH", { token, user });
      return user;
    },

    /**
     * JC Seeker Registration.
     */
    async jcRegisterSeeker({ commit, dispatch }, payload) {
      dispatch("logout");
      const response = await api.post("/jc-auth/register/seeker", payload);
      const { token, refreshToken, user, companyId } = response.data;

      // Auto-login after registration — store auth data so profile page has access
      if (token) {
        localStorage.setItem("jwt", token);
        if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("userdata", JSON.stringify(user));
        if (companyId) localStorage.setItem("companyId", String(companyId));
        commit("SET_AUTH", { token, user });
      }

      return response.data;
    },

    /**
     * JC Employer Registration.
     */
    async jcRegisterEmployer({ commit, dispatch }, payload) {
      dispatch("logout");
      const response = await api.post("/jc-auth/register/employer", payload);
      const { token, refreshToken, user, companyId } = response.data;

      if (token) {
        localStorage.setItem("jwt", token);
        if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("userdata", JSON.stringify(user));
        if (companyId) localStorage.setItem("companyId", String(companyId));
        commit("SET_AUTH", { token, user });
      }

      return response.data;
    },

    /**
     * Clear auth state and redirect to login. With `revoke` (an explicit
     * sign-out) the server session is ended too, so a copied refresh token
     * stops working.
     */
    logout({ commit }, { revoke = false } = {}) {
      const token = localStorage.getItem("jwt");
      if (revoke && token) endServerSession(token);

      localStorage.removeItem("jwt");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      localStorage.removeItem("userdata");
      localStorage.removeItem("companyId");

      commit("CLEAR_AUTH");
    },
  },
});
